import { filterCatalogs, filterCatalogsByReg, filterItems, filterSellers, sortItems } from './sorting';
import { getCatalogCardDatas, getItemCardDatas, getLiveItemCardDatas, getSellerCardDatas } from './cardData';
import { getItemIdsForCatalog, getItemIdsForSeller } from './item';
import { createSelector } from 'reselect';
import { getAllCurrentUserCatalogRegistrationIds } from './catalogRegistration';
import { getCatalogIdsForSeller } from './catalog';
import { getFollowedSellerIds } from './followSeller';
import { getLiveCatalogData } from '@/redux/modules/console';
import { getMyBidItemIds } from './biddingInfo';
import { getSavedSearchItemIds } from './savedSearch';
import { getSortedSavedItemIdsFromState } from './saveItem';
import { getStorefrontItemIds } from './storefrontPage';
import { itemSortOrders } from '@/enums';

type GetCatalogItemsParams = {
    buyNow?: boolean;
    count?: number | 'all';
    live?: boolean;
    page?: number;
    sort?: string;
    terms?: string;
};
export const getCatalogItems = (
    state: any,
    catalogId: number,
    { buyNow, count, live, page, sort, terms }: GetCatalogItemsParams = {}
) => {
    const ids = getItemIdsForCatalog(state, catalogId);
    const all = getItemCardDatas(state, ids);
    const filterBuyNow = buyNow
        ? all.filter((x) => x.buyNow.status === 'available')
        : Boolean(live)
        ? all.filter(
              (x) =>
                  Boolean(x.biddingInfo.isAvailable) &&
                  !x.biddingInfo.isPassed &&
                  !x.biddingInfo.isSold &&
                  ((x.biddingInfo.isLocked && x.liveItemStatus.open) ||
                      (!x.biddingInfo.isLocked && !x.liveItemStatus.open))
          )
        : all;
    return filterItems(filterBuyNow, { count, page, sort, terms });
};

export const getNextCatalogItemIds = (state: any, catalogId: number, itemId: number, count: number) => {
    // @ts-ignore
    const { items } = getCatalogItems(state, catalogId, { sort: 'LOT_NUMBER_LOWEST_FIRST' }) || [];
    const [theItem] = items.filter(({ item }) => item.itemId === itemId);

    let returnItems: any = items.filter(({ item }) => {
        return item.index > theItem?.item?.index;
    });

    if (returnItems.length < count) {
        return [...returnItems, ...items.slice(0, count - returnItems.length)].map(({ itemId }) => itemId);
    } else {
        return returnItems.slice(0, count).map(({ itemId }) => itemId);
    }
};

export const getCatalogContainsBuyNowLots = (state: any, catalogId: number) => {
    const ids = getItemIdsForCatalog(state, catalogId);
    const all = getItemCardDatas(state, ids);
    const buyNowItems = all.filter((x) => x.buyNow.status === 'available').length > 0;
    return buyNowItems;
};

type GetLiveCatalogItemsParams = {
    count?: number | 'all';
    page?: number;
    sort?: string;
    terms?: string;
};
export const getLiveCatalogItems = (
    state: any,
    catalogId: number,
    { count, page, sort, terms }: GetLiveCatalogItemsParams = {}
) => {
    const ids = getItemIdsForCatalog(state, catalogId);
    const all = getLiveItemCardDatas(state, ids);
    return filterItems(all, { count, page, sort, terms });
};

type GetFollowedSellersParams = {
    count?: number | 'all';
    page?: number;
    sort?: string;
    terms?: string;
};
export const getFollowedSellers = (state: any, { count, page, sort, terms }: GetFollowedSellersParams = {}) => {
    const ids = getFollowedSellerIds(state);
    const all = getSellerCardDatas(state, ids);
    return filterSellers(all, { count, page, sort, terms });
};

type GetMyBidsParams = {
    count?: number | 'all';
    filter?: string;
    page?: number;
    sort?: string;
    tab?: string;
    terms?: string;
};
export const getMyBids = (state: any, { count, filter, page, sort, tab, terms }: GetMyBidsParams = {}) => {
    const { all, past, upcoming } = getMyBidItemIds(state);
    const ids = tab === 'upcoming' ? upcoming : tab === 'ended' ? past : all;
    const cardData = getItemCardDatas(state, ids);
    let filteredItems;
    if (sort === 'TIME_SOONEST_FIRST') {
        filteredItems = stackSortedLiveSavedItems(state, cardData, { count, filter, page, sort, terms });
    } else {
        filteredItems = filterItems(cardData, { count, filter, page, sort, terms });
    }
    return filteredItems;
};

type GetMySavedItemsParams = {
    count?: number | 'all';
    page?: number;
    sort?: string;
    terms?: string;
};
export const getMySavedItems = (state: any, { count, page, sort, terms }: GetMySavedItemsParams = {}) => {
    const ids = getSortedSavedItemIdsFromState(state);
    const all = getItemCardDatas(state, ids);
    let filteredItems;
    if (sort === 'TIME_SOONEST_FIRST') {
        filteredItems = stackSortedLiveSavedItems(state, all, { count, page, sort, terms });
    } else {
        filteredItems = filterItems(all, { count, page, sort, terms });
    }
    return filteredItems;
};

type StackSortedLiveSavedItemsParams = {
    count?: number | 'all';
    filter?: string;
    page?: number;
    sort?: string;
    tab?: string;
    terms?: string;
};
const stackSortedLiveSavedItems = (
    state: any,
    all,
    { count, filter, page, sort, tab, terms }: StackSortedLiveSavedItemsParams
) => {
    const liveItems = [];
    const savedItems = [];
    const pastItems = [];
    all.forEach((item) => {
        const { currentItemIndex } = getLiveCatalogData(state, item.catalog.catalogId);
        const lotsAway = item.item.index - currentItemIndex;
        if (currentItemIndex && lotsAway > -1) {
            item.lotsAway = lotsAway;
            liveItems.push(item);
        } else if (!item.biddingInfo.isAvailable) {
            pastItems.push(item);
        } else {
            savedItems.push(item);
        }
    });
    const sortedLiveItems = filterItems(liveItems, { sort: 'LOTS_AWAY_FIRST' });
    const sortedPastItems = filterItems(pastItems, { sort: 'TIME_ENDING_FURTHEST' });
    const sortedSavedItems = filterItems(savedItems, { sort });
    return filterItems([...sortedLiveItems.items, ...sortedSavedItems.items, ...sortedPastItems.items], {
        count,
        filter,
        page,
        tab,
        terms,
    });
};

type GetMySavedSearchItemsParams = {
    count?: number | 'all';
    page?: number;
    sort?: string;
    terms?: string;
};

export const getMySavedSearchItemIds = (state: any, { count, page, sort, terms }: GetMySavedSearchItemsParams = {}) => {
    const ids = getSavedSearchItemIds(state);
    const all = getItemCardDatas(state, ids);
    const results = filterItems(all, { count, page, sort, terms });
    return results.itemIds.length && results.itemIds.length === ids.length ? results.itemIds : ids;
};

type GetSellerCatalogsParams = {
    count?: number | 'all';
    page?: number;
    sort?: string;
    tab?: string;
    terms?: string;
};
export const getSellerCatalogs = (
    state: any,
    sellerId: number,
    { count, page, sort, tab, terms }: GetSellerCatalogsParams = {}
) => {
    const ids = getCatalogIdsForSeller(state, sellerId);
    const all = getCatalogCardDatas(state, ids);
    return filterCatalogs(all, { count, page, sort, tab, terms });
};

type GetStorefrontItemsParams = {
    buyNow?: boolean;
    count?: number | 'all';
    page?: number;
    sort?: string;
    terms?: string;
};
export const getStorefrontItems = (state: any, { buyNow, count, page, sort, terms }: GetStorefrontItemsParams = {}) => {
    const ids = getStorefrontItemIds(state);
    const all = getItemCardDatas(state, ids);
    const filterBuyNow = buyNow ? all.filter((x) => x.buyNow.status === 'available') : all;
    return filterItems(filterBuyNow, { count, page, sort, terms });
};

type GetMyAuctionsParams = {
    count?: number | 'all';
    page?: number;
    sort?: string;
    tab?: string;
    terms?: string;
};
export const getMyAuctions = (state: any, { count, page, sort, tab, terms }: GetMyAuctionsParams = {}) => {
    const catalogIds = getAllCurrentUserCatalogRegistrationIds(state);
    const all = getCatalogCardDatas(state, catalogIds);
    return filterCatalogsByReg(state, all, { count, page, sort, tab, terms });
};

export const getMostPopularItemsByBids = (state: any, sellerId: number) => {
    const ids = getItemIdsForSeller(state, sellerId);
    const all = getItemCardDatas(state, ids);
    const { itemIds } = filterItems(all, { count: 20, page: 1, sort: 'MOST_BIDS', tab: 'upcoming' });
    return itemIds || [];
};

export const comingUpItemsSelector = createSelector(getMySavedItems, getMyBids, (savedItems, myBidItems) => {
    const ids = [];
    let comingUpItems = [];
    //This logic is needed to filter out duplicates. An item could be both saved and bid on, and we only want to display it once.
    savedItems.items.forEach((item) => {
        if (item.biddingInfo.isAvailable) {
            ids.push(item.item.itemId);
            comingUpItems.push(item);
        }
    });
    myBidItems.items.forEach((item) => {
        if (item.biddingInfo.isAvailable && !ids.includes(item.item.itemId)) {
            comingUpItems.push(item);
        }
    });
    comingUpItems = sortItems(comingUpItems, itemSortOrders.DATE_ASCENDING);
    return comingUpItems;
});

export const getCatalogsForFollowedSellers = (state: any): number[] => {
    const followedSellerIds = getFollowedSellerIds(state);
    let followedSellerCatalogs = [];
    let followedSellerCatalogIds = [];
    // limiting the number of items per seller to keep data small
    followedSellerIds.forEach((id) => {
        const liveCatalogs = getSellerCatalogs(state, id, {
            count: 20,
            page: 1,
            sort: 'DATE_ASCENDING',
            tab: 'live',
        });
        if (liveCatalogs && liveCatalogs.items && liveCatalogs.items.length > 0) {
            followedSellerCatalogs = followedSellerCatalogs.concat(liveCatalogs.items);
            liveCatalogs.items.forEach((item) => {
                if (item && item.catalog && item.catalog.catalogId) {
                    followedSellerCatalogIds = followedSellerCatalogIds.concat(item.catalog.catalogId);
                }
            });
        }
    });

    return followedSellerCatalogIds.slice(0, 16);
};
